import Personal, { PERSON } from "../../InmateDetails/Personal";
import AdditionalPersonalInformation from "./AdditionalPersonalInformation";
import AliasesList from "./AliasesList";
import { BookingsSectionTitle } from "../styles";
import PlaceBirthForm from "./PlaceBirthForm";
import DistinguishingCharacteristics from "./DistinguishingCharacteristics";
import PersonalMiscNumbersInfo from "./PersonalMiscNumbersInfo";
import { forwardRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setBookings } from "@/slices/bookingsSlice";
import dayjs from "dayjs";
import AddressesList from "./AddressesList";
import { StyledHeadingBar } from "@/common/styles";
import { hasCheckSecurity, isNonEmptyVariable } from "@/util/common_function";
import EmergencyForm from "./EmergencyForm";
import ActiveStaffMemberForm from "./ActiveStaffMemberForm";
import { Arbitrary_Names, Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { useClientParamBool } from "@/hooks/useClientParam";

// ASP defaults IncludeBioExtra to True for most modules, False for Staff
// (see BioInterface.asp ClientGetClientParam("Staff","IncludeBioExtra","False")).
const INCLUDE_BIO_EXTRA_DEFAULTS = {
  Staff: false,
};

const PersonalInformationDetails = forwardRef(
  ({
    allFormRef,
    isStaff = false,
    showMainHeading = false,
    isBoxLayout = false,
    detailLevel = PERSON.BOOKINGS,
    startingPoint = "",
    highlightFields=[],
  }, ref) => {
    const dispatch = useDispatch();
    const { bookings, bookingsFieldsEnabled } = useSelector(
      (state) => state?.bookings
    );

    const [personalInfo, setPersonalInfo] = useState(null);
    const [placeBirthInfo, setPlaceBirthInfo] = useState(null);
    const [emergencyDetails, setEmergencyDetails] = useState(null);
    const [extraPersonalInfo, setExtraPersonalInfo] = useState({});
    // Assume BioExtra has fields until AdditionalPersonalInformation's own
    // column-defs fetch resolves — avoids a hide-then-show flicker of the
    // "Additional Personal Information" heading in the common case where
    // the customer does have BioExtra fields configured.
    const [hasBioExtraFields, setHasBioExtraFields] = useState(true);

    useEffect(() => {
      const bio = bookings?.bio?.[0];

      setPersonalInfo({
        ...bio,
        dob: bio?.dob ? dayjs(bio?.dob) : null,
        lastModified: bio?.lastModified ? dayjs(bio?.lastModified) : null,
      });

      setEmergencyDetails({
        ...bio,
      });

      setPlaceBirthInfo({
        fbi: bio?.fbi,
        sid: bio?.sid,
        pobType: isNonEmptyVariable(bio?.pobType)
          ? parseInt(bio?.pobType)
          : null,
        pobNciCid: bio?.pobNciCid,
        countryOfCitizenshipId: bio?.countryOfCitizenshipId,
        primaryLanguage: bio?.primaryLanguage,
      });

      // Set initial extra personal info (if any) from bookings
      setExtraPersonalInfo(bookings?.bio?.[0]?.bioExtra?.[0] || null);
    }, [bookings]);

    // Update bio (the main personal information) in Redux
    const updateForm = (value) => {
      dispatch(
        setBookings({
          ...bookings,
          bio: [
            {
              ...bookings?.bio?.[0],
              ...value,
            },
          ],
        })
      );
    };

    const emergencyArbitary = hasCheckSecurity(
                                Security_Types.functionality,
                                Entity_types.arbitary,
                                LABELS_CAPITAL.VIEW,
                                Arbitrary_Names.divPersonalInfoEmergency
                              );

    // client-param-driven Bio section visibility (mirrors ASP's BioInterface.asp)
    const multipleAliasesEnabled = useClientParamBool("Bio", "MultipleAliases", false);
    const bioExtraContext = startingPoint || "Bookings";
    const includeBioExtra = useClientParamBool(
      bioExtraContext,
      "IncludeBioExtra",
      INCLUDE_BIO_EXTRA_DEFAULTS[bioExtraContext] ?? true
    );

    const handleDCChange = (value) => {
      dispatch(
        setBookings({
          bio: [{ ...bookings?.bio?.[0], ...value }],
        })
      );
    };

    return (
      <>
        <div
          style={{
            border: isBoxLayout ? "2px solid rgba(0, 0, 0, 0.15)" : "none",
            marginBottom: isBoxLayout ? "25px" : "0",
          }}
        >
          {showMainHeading && (
            <StyledHeadingBar style={{ justifyContent: "space-between" }}>
              <div className="main-title">Personal Information</div>
              {/* <FolderFlagGroup showIconOnly/> */}
            </StyledHeadingBar>
          )}

          <div
            style={{
              padding: isBoxLayout ? "16px" : "0",
              maxHeight: isBoxLayout ? "350px" : "auto",
              overflow: isBoxLayout ? "auto" : "inherit",
            }}
          >
            <Personal
              formData={personalInfo}
              person={detailLevel}
              personalFormRef={allFormRef?.personalFormRef}
              updateForm={updateForm}
              startingPoint={startingPoint}
              highlightFields={highlightFields}
            />
            {detailLevel !== "LOW" && (
              <>
                {/* Aliases — Bio/MultipleAliases client param toggles the full
                    list vs. the single "Aliases" field rendered by <Personal> */}
                {!isStaff && multipleAliasesEnabled && (
                  <>
                    <BookingsSectionTitle
                      style={{ marginBottom: "10px", marginTop: "15px" }}
                    >
                      <span>List of aliases</span>
                    </BookingsSectionTitle>
                    <AliasesList
                      personalFormAliasesRef={
                        allFormRef?.personalFormAliasesRef
                      }
                      startingPoint={startingPoint}
                    />
                  </>
                )}

                {/* Addresses */}
                <BookingsSectionTitle
                  style={{ marginBottom: "10px", marginTop: "15px" }}
                >
                  <span>List of addresses</span>
                </BookingsSectionTitle>
                <AddressesList startingPoint={startingPoint} />

                {!isStaff ? (
                  <>
                    {/* Place of Birth */}
                    <BookingsSectionTitle
                      style={{ marginBottom: "10px", marginTop: "20px" }}
                    >
                      <span>Place of Birth</span>
                    </BookingsSectionTitle>
                    <PlaceBirthForm
                      formData={placeBirthInfo}
                      updateForm={updateForm}
                      startingPoint={startingPoint}
                      bookingsFieldsEnabled={bookingsFieldsEnabled}
                      personalFormPlaceOfBirthRef={
                        allFormRef?.personalFormPlaceOfBirthRef
                      }
                    />

                    {/* Distinguishing Characteristics */}
                    <BookingsSectionTitle
                      style={{ marginBottom: "10px", marginTop: "15px" }}
                    >
                      <span>Distinguishing Characteristics</span>
                    </BookingsSectionTitle>
                    <DistinguishingCharacteristics
                      personalFormDistinguishingRef={allFormRef?.personalFormDistinguishingRef}
                      bioData={bookings?.bio?.[0]}          // ← pass bio directly
                      fieldsEnabled={bookingsFieldsEnabled}
                      onBioChange={handleDCChange}
                      startingPoint={startingPoint}
                    />

                    {/* Misc Numbers */}
                    <BookingsSectionTitle
                      style={{ marginBottom: "10px", marginTop: "25px" }}
                    >
                      <span>Misc Numbers</span>
                    </BookingsSectionTitle>
                    <PersonalMiscNumbersInfo startingPoint={startingPoint} />

                    {/* Additional Personal Information — gated by the
                        <module>/IncludeBioExtra client param, and further
                        hidden once BioExtra's own field defs come back empty
                        (no configured columns to show). */}
                    {includeBioExtra && hasBioExtraFields && (
                      <>
                        <BookingsSectionTitle
                          style={{ marginBottom: "10px", marginTop: "20px" }}
                        >
                          <span>Additional Personal Information</span>
                        </BookingsSectionTitle>
                        <AdditionalPersonalInformation
                          onFieldsLoaded={setHasBioExtraFields}
                        />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {emergencyArbitary && (
                      <>
                        {/* Emergency Details */}
                        <BookingsSectionTitle
                          style={{ marginBottom: "10px", marginTop: "20px" }}
                        >
                          <span>Emergency Details</span>
                        </BookingsSectionTitle>
                        <EmergencyForm
                          formData={emergencyDetails}
                          updateForm={updateForm}
                          bookingsFieldsEnabled={bookingsFieldsEnabled}
                          startingPoint={startingPoint}
                        />
                      </>
                    )}

                    {/* Staff Status — always shown regardless of Emergency Details visibility */}
                    <BookingsSectionTitle
                      style={{ marginBottom: "10px", marginTop: "20px" }}
                    >
                      <span>Staff Status</span>
                    </BookingsSectionTitle>
                    <ActiveStaffMemberForm
                      bookingsFieldsEnabled={bookingsFieldsEnabled}
                      startingPoint={startingPoint}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </>
    );
  }
);

export default PersonalInformationDetails;
